import { Box, Card, InlineStack, Text } from '@shopify/polaris';
import { CartesianGrid, ComposedChart, Legend, Line, ReferenceLine, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis } from 'recharts';
import { formatCompactCurrency, formatShortDate, toNumber } from './analyticsHelpers';

/*
  Pieces of the cash planning tab that the executive dashboard shows as well:
  the balance chart with its event markers, and the pure computations that turn
  the /api/cash-planning/plan response into the chart data for a horizon and a
  set of layer toggles. Kept in one place so both views always tell the same story.

  Expected and cautious leave the intercompany documents (other group companies)
  out; the dotted "incl. intercompany" line adds them back.
*/

const eur0 = new Intl.NumberFormat('nl-BE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
export const eur = (value) => eur0.format(toNumber(value));

export const EXPECTED_STROKE = '#2E5AAC';
export const CAUTIOUS_STROKE = '#B54708';
export const ACTUAL_STROKE = '#1A7F37';
export const INTERCOMPANY_STROKE = '#8A5CD0';

// weekly table / chart layers (keys = components in the API response)
// `source` is shown under the column heading so it is clear where each number comes from
export const COMPONENTS = [
  { key: 'customer_invoices', label: 'Customer invoices', types: ['ar_invoice', 'sales_credit_note'], source: 'open invoices' },
  { key: 'sales_orders', label: 'Sales orders', types: ['sales_order'], toggle: true, hint: 'confirmed orders not invoiced yet', source: 'order backlog' },
  { key: 'supplier_invoices', label: 'Supplier invoices', types: ['ap_invoice', 'committed_payment', 'purchase_credit_note'], source: 'open invoices' },
  { key: 'purchase_orders', label: 'Purchase orders', types: ['purchase_order'], toggle: true, hint: 'not received yet', source: 'open orders' },
  { key: 'straight_loan', label: 'Straight loans', types: ['straight_loan'], source: 'bank text' },
  { key: 'loan', label: 'Bank loans', categories: ['loan'], source: 'bank history' },
  { key: 'salary', label: 'Salaries', categories: ['salary'], source: 'bank history' },
  { key: 'social_tax', label: 'Social secr. & taxes', categories: ['social', 'tax'], source: 'bank history' },
  { key: 'other_recurring', label: 'Other recurring', categories: ['card', 'insurance', 'bank_fees', 'expenses'], source: 'bank history' },
  { key: 'planned', label: 'Planned', types: ['planned'], source: 'manual' }
];

export const ITEM_TYPES = {
  ar_invoice: { label: 'Customer invoice', tone: 'success', linkBase: '/invoices' },
  sales_credit_note: { label: 'Credit note out', tone: 'warning', linkBase: '/creditnotes' },
  sales_order: { label: 'Sales order', tone: 'info', linkBase: '/sales' },
  ap_invoice: { label: 'Supplier invoice', tone: 'warning', linkBase: '/invoices_in' },
  committed_payment: { label: 'Committed payment', tone: 'critical', linkBase: null },
  purchase_credit_note: { label: 'Credit note in', tone: 'success', linkBase: '/creditnotes_in' },
  purchase_order: { label: 'Purchase order', tone: 'attention', linkBase: '/purchases' },
  straight_loan: { label: 'Straight loan', tone: 'critical', linkBase: null }
};

// events marked on the balance chart (numbered vertical lines + the chips underneath)
export const EVENT_TYPES = {
  straight_loan: { label: 'Straight loan repayment', color: '#B54708' },
  loan_in: { label: 'Simulated loan drawdown', color: '#1A7F37' },
  loan_out: { label: 'Simulated loan repayment', color: '#B54708' },
  planned: { label: 'Planned item', color: '#7C3AED' },
  large: { label: 'Large single item', color: '#6B7280' }
};

export const addDaysIso = (iso, days) => new Date(new Date(`${iso}T00:00:00Z`).getTime() + days * 86400000).toISOString().substring(0, 10);

//----------------------------------------------------------------------------
// computations on the plan response
//----------------------------------------------------------------------------
// expected balance path re-summed from the per-component daily flows (so layers can be toggled); without intercompany
export function buildExpectedSeries(plan, layers) {
  const series = [];
  let balance = plan.openingBalance;
  for (let offset = 0; offset < plan.daily.length; offset++) {
    for (const component of COMPONENTS) {
      if (component.toggle && !layers[component.key]) continue;
      balance += plan.componentsDaily[component.key]?.[offset] || 0;
    }
    series.push(Math.round(balance));
  }
  return series;
}

// the expected path plus the open intercompany documents (Aha Innovations, Jadibo, Adriaens NV) of the selected layers
export function buildIntercompanySeries(plan, layers, expectedSeries) {
  const series = [];
  let added = 0;
  for (let offset = 0; offset < expectedSeries.length; offset++) {
    for (const component of COMPONENTS) {
      if (component.toggle && !layers[component.key]) continue;
      added += plan.intercompanyDaily?.[component.key]?.[offset] || 0;
    }
    series.push(Math.round(expectedSeries[offset] + added));
  }
  return series;
}

// reconstructed past balances followed by the expected, incl. intercompany and cautious paths up to the horizon
export function buildBalanceChartData(plan, horizon, expectedSeries, intercompanySeries = expectedSeries) {
  const past = plan.pastDaily.filter((p) => p.date < plan.today).map((p) => ({ date: p.date, actual: p.balance }));
  const future = plan.daily.slice(0, horizon + 1).map((d, offset) => ({
    date: d.date,
    expected: expectedSeries[offset],
    withIntercompany: intercompanySeries[offset],
    cautious: d.cautious,
    actual: offset === 0 ? plan.openingBalance : undefined
  }));
  return [...past, ...future];
}

// markers: straight-loan repayments, simulated loan legs, planned one-offs, then the largest single items (12 at most)
export function collectChartEvents(plan, { horizon, layers, eventThreshold }) {
  const horizonEnd = addDaysIso(plan.today, horizon);
  const list = [];
  for (const item of plan.items) {
    // intercompany documents are not in the expected line, so they get no marker either
    if (!item.expectedDate || item.expectedDate > horizonEnd || item.intercompany) continue;
    if (item.type === 'straight_loan') {
      if (String(item.id).endsWith('_interest')) continue;
      list.push({ date: item.expectedDate, type: 'straight_loan', label: item.number, amount: item.amount, priority: 0 });
      continue;
    }
    const component = COMPONENTS.find((c) => c.types?.includes(item.type));
    if (component?.toggle && !layers[component.key]) continue;
    if (eventThreshold && Math.abs(item.amount) >= eventThreshold) {
      list.push({ date: item.expectedDate, type: 'large', label: `${ITEM_TYPES[item.type]?.label || item.type} ${item.number} · ${item.partnerName || ''}`, amount: item.amount, priority: 2 });
    }
  }
  for (const p of plan.plannedItems) {
    if (p.date > horizonEnd) continue;
    if (p.kind === 'loan') list.push({ date: p.date, type: p.phase === 'drawdown' ? 'loan_in' : 'loan_out', label: `${p.label} (${p.phase})`, amount: p.amount, priority: 0 });
    else if (p.kind === 'one_off') list.push({ date: p.date, type: 'planned', label: p.label, amount: p.amount, priority: 1 });
  }
  return list
    .sort((a, b) => a.priority - b.priority || Math.abs(b.amount) - Math.abs(a.amount))
    .slice(0, 12)
    .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0))
    .map((event, index) => ({ ...event, n: index + 1 }));
}

// end balance, lowest point and money in/out over the horizon for the selected layers (money in/out without intercompany)
export function summarizeHorizon(plan, { horizon, layers, expectedSeries, intercompanySeries = expectedSeries }) {
  const window = plan.daily.slice(0, horizon + 1);
  let lowest = { date: plan.today, balance: expectedSeries[0] };
  for (let offset = 0; offset <= horizon; offset++) if (expectedSeries[offset] < lowest.balance) lowest = { date: window[offset].date, balance: expectedSeries[offset] };
  let lowestWithIntercompany = { date: plan.today, balance: intercompanySeries[0] };
  for (let offset = 0; offset <= horizon; offset++)
    if (intercompanySeries[offset] < lowestWithIntercompany.balance) lowestWithIntercompany = { date: window[offset].date, balance: intercompanySeries[offset] };
  let lowestCautious = window[0];
  for (const d of window) if (d.cautious < lowestCautious.cautious) lowestCautious = d;
  let inflow = 0;
  let outflow = 0;
  for (const component of COMPONENTS) {
    if (component.toggle && !layers[component.key]) continue;
    const flows = plan.componentsDaily[component.key] || [];
    for (let offset = 1; offset <= horizon; offset++) {
      if (flows[offset] > 0) inflow += flows[offset];
      else outflow += flows[offset] || 0;
    }
  }
  return {
    end: expectedSeries[horizon],
    endCautious: window[horizon].cautious,
    endWithIntercompany: intercompanySeries[horizon],
    lowest,
    lowestCautious,
    lowestWithIntercompany,
    inflow: Math.round(inflow),
    outflow: Math.round(outflow)
  };
}

//----------------------------------------------------------------------------
// chart
//----------------------------------------------------------------------------
function BalanceTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  const point = payload[0].payload;
  return (
    <Card>
      <Text as="p" variant="bodySm" fontWeight="semibold">
        {formatShortDate(label)}
      </Text>
      {point.actual != null && <p className="label">{`Actual: ${eur(point.actual)}`}</p>}
      {point.expected != null && <p className="label">{`Expected: ${eur(point.expected)}`}</p>}
      {point.withIntercompany != null && point.withIntercompany !== point.expected && <p className="label">{`Incl. intercompany: ${eur(point.withIntercompany)}`}</p>}
      {point.cautious != null && <p className="label">{`Cautious: ${eur(point.cautious)}`}</p>}
    </Card>
  );
}

export function BalanceChart({ data, events, height = 360 }) {
  // one marker per day; several events on the same day share the line and list their numbers
  const byDate = new Map();
  for (const event of events) {
    if (!byDate.has(event.date)) byDate.set(event.date, []);
    byDate.get(event.date).push(event);
  }
  return (
    <div style={{ width: '100%', height: `${height}px` }}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 15, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="1 5" />
          <XAxis dataKey="date" tickFormatter={formatShortDate} minTickGap={40} />
          <YAxis tickFormatter={formatCompactCurrency} width={80} />
          <ChartTooltip content={<BalanceTooltip />} />
          <Legend />
          <ReferenceLine y={0} stroke="#AD0002" strokeDasharray="4 4" />
          {[...byDate.entries()].map(([date, dayEvents]) => (
            <ReferenceLine
              key={date}
              x={date}
              stroke={EVENT_TYPES[dayEvents[0].type].color}
              strokeDasharray="3 3"
              label={{ value: dayEvents.map((e) => e.n).join(','), position: 'top', fill: EVENT_TYPES[dayEvents[0].type].color, fontSize: 11, fontWeight: 700 }}
            />
          ))}
          <Line type="monotone" dataKey="actual" stroke={ACTUAL_STROKE} strokeWidth={2} dot={false} isAnimationActive={false} name="Actual balance" />
          <Line type="monotone" dataKey="expected" stroke={EXPECTED_STROKE} strokeWidth={2.5} dot={false} isAnimationActive={false} name="Expected (excl. intercompany)" />
          <Line
            type="monotone"
            dataKey="withIntercompany"
            stroke={INTERCOMPANY_STROKE}
            strokeWidth={1.5}
            strokeDasharray="2 3"
            dot={false}
            isAnimationActive={false}
            name="Expected incl. intercompany"
          />
          <Line type="monotone" dataKey="cautious" stroke={CAUTIOUS_STROKE} strokeWidth={2} strokeDasharray="6 4" dot={false} isAnimationActive={false} name="Cautious" />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

export function EventChips({ events }) {
  if (!events.length) return null;
  return (
    <Box paddingBlockStart="200">
      <InlineStack gap="200" wrap>
        {events.map((event) => (
          <span key={event.n} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '2px 8px', borderRadius: 12, background: '#F1F2F4', fontSize: 12 }}>
            <span style={{ fontWeight: 700, color: EVENT_TYPES[event.type].color }}>{event.n}</span>
            <span>{formatShortDate(event.date)}</span>
            <span style={{ color: '#616161' }}>{event.label}</span>
            <span style={{ color: event.amount < 0 ? '#8E1F0B' : '#0C5132', fontWeight: 600 }}>{eur(event.amount)}</span>
          </span>
        ))}
      </InlineStack>
    </Box>
  );
}
